import { ConfigProvider } from "antd";
import { Routes, Route, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "./app-insights";
import * as Styles from "./styles";
import { colors } from "./styles";
import { ReactComponent as HeaderLogo } from "./icons/header-logo.svg";
import API, { OrchestratorAPI } from "../common/api/axios";
import { authEnabled, msalInstance } from "../common/auth/authConfig";
import { refreshDurationMs, TENANT_HEADERS } from "../common/config";
import { AppDataProvider, ScannerCounts } from "./appContext";
import Home from "../pages/home/Home";
import OrderToteMapping from "../pages/order-tote-mapping/OrderToteMapping";
import Inward from "../pages/inward/Inward";
import CycleCount from "../pages/cycle-count/CycleCount";

const scannerTheme = {
  token: {
    fontSize: 16,
    controlHeight: 48,
    borderRadius: 10,
    colorPrimary: colors.primary,
  },
  components: {
    Button: { controlHeight: 56, fontSizeLG: 18 },
  },
};

// Active-task predicate shared with the Operator UI (App.tsx:53-61).
const isActiveTask = (task: any): boolean =>
  !!task?.id &&
  task?.isComplete !== true &&
  task?.isCancelled !== true &&
  task?.isAborted !== true;

const isActiveOrderToteMappingTask = (task: any): boolean =>
  isActiveTask(task) && !!task?.orderToteMappingTask;

// The scanner's inward flow is order-driven, so the home badge counts pending
// inward ORDERS (InwardOrder/pending) rather than dispatched operator tasks.
const isPendingInwardOrder = (order: any): boolean =>
  !!order?.inwardOrderReferenceId;

const MODULE_TITLES: Record<string, string> = {
  "/order-tote-mapping": "Order Fulfillment",
  "/inward": "Material Inwards",
  "/cycle-count": "Cycle Count",
};

function App() {
  const location = useLocation();
  const [configName, setConfigName] = useState<string | undefined>(undefined);
  const [counts, setCounts] = useState<ScannerCounts>({
    orderToteMapping: 0,
    inward: 0,
    cycleCount: 0,
  });
  const [now, setNow] = useState<Date>(() => new Date());
  const [inwardOrchestrationActive, setInwardOrchestrationActive] =
    useState(false);
  const isMountedRef = useRef(true);

  const accountName = authEnabled
    ? msalInstance?.getActiveAccount()?.name ??
      msalInstance?.getActiveAccount()?.username
    : undefined;

  const signOut = useCallback(() => {
    if (authEnabled && msalInstance) {
      msalInstance.logoutRedirect();
    }
  }, []);

  // Config fetch (drives the HJI inward branch; mirrors Operator App.tsx:181-208).
  useEffect(() => {
    isMountedRef.current = true;
    const fetchConfig = async () => {
      try {
        const res = await API.get("/Configuration/config", {
          headers: TENANT_HEADERS,
        });
        if (isMountedRef.current) {
          setConfigName(
            res.status === 200 && typeof res.data?.configName === "string"
              ? res.data.configName
              : ""
          );
        }
      } catch {
        if (isMountedRef.current) {
          setConfigName("");
        }
      }
    };
    fetchConfig();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchCounts = useCallback(async () => {
    const readCount = async (
      url: string,
      predicate: (task: any) => boolean
    ): Promise<number | null> => {
      try {
        const res = await API.get(url, { headers: TENANT_HEADERS });
        if (res.status === 200 && Array.isArray(res.data)) {
          return res.data.filter(predicate).length;
        }
      } catch {
        // ignore — leave the previous count in place
      }
      return null;
    };

    const [mapping, inward, cycle] = await Promise.all([
      readCount("PickStation/orderToteMappingTasks", isActiveOrderToteMappingTask),
      readCount("InwardOrder/pending", isPendingInwardOrder),
      readCount("PickStation/cycleCountTasks", isActiveTask),
    ]);

    if (!isMountedRef.current) {
      return;
    }

    setCounts((prev) => ({
      orderToteMapping: mapping ?? prev.orderToteMapping,
      inward: inward ?? prev.inward,
      cycleCount: cycle ?? prev.cycleCount,
    }));
  }, []);

  // Whether an inward operation is running — same predicate the Inward page uses
  // (Inward.tsx checkOrchestration). Drives the Home "exit inward" cue that fires
  // once all inward tasks are done but the operation is still open.
  const checkInwardOrchestration = useCallback(async () => {
    try {
      const res = await OrchestratorAPI.get("orchestrations", {
        headers: TENANT_HEADERS,
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const active = res.data.some(
          (orch: any) =>
            orch.operationMode === "Inward" &&
            orch.status !== "Completed" &&
            new Date(orch.creationDate) >= oneDayAgo
        );
        if (isMountedRef.current) {
          setInwardOrchestrationActive(active);
        }
      }
    } catch {
      if (isMountedRef.current) {
        setInwardOrchestrationActive(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    checkInwardOrchestration();
    const interval = setInterval(() => {
      fetchCounts();
      checkInwardOrchestration();
    }, refreshDurationMs());
    return () => clearInterval(interval);
  }, [fetchCounts, checkInwardOrchestration]);

  // Lightweight clock tick.
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  const moduleTitle = MODULE_TITLES[location.pathname];
  const time = format(now, "hh:mm a");
  const date = format(now, "MMM dd");

  const appData = useMemo(
    () => ({
      configName,
      counts,
      inwardOrchestrationActive,
      accountName,
      authEnabled,
      signOut,
    }),
    [configName, counts, inwardOrchestrationActive, accountName, signOut]
  );

  return (
    <ConfigProvider theme={scannerTheme}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <AppDataProvider value={appData}>
          <Styles.Shell>
            <Styles.Header>
              <Styles.HeaderTopRow>
                <Styles.Brand>
                  <HeaderLogo />
                  <Styles.BrandTitle>Carte+ Handheld</Styles.BrandTitle>
                </Styles.Brand>
                <Styles.Clock>
                  <Styles.ClockTime>{time}</Styles.ClockTime>
                  <Styles.ClockDate>{date}</Styles.ClockDate>
                </Styles.Clock>
              </Styles.HeaderTopRow>
              <Styles.HeaderMetaRow>
                {configName ? (
                  <Styles.ConfigName>{configName}</Styles.ConfigName>
                ) : null}
                {moduleTitle && (
                  <Styles.ModuleBadge>{moduleTitle}</Styles.ModuleBadge>
                )}
                {accountName && (
                  <Styles.AccountName>{accountName}</Styles.AccountName>
                )}
              </Styles.HeaderMetaRow>
            </Styles.Header>
            <Styles.Main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route
                  path="/order-tote-mapping"
                  element={<OrderToteMapping />}
                />
                <Route path="/inward" element={<Inward />} />
                <Route path="/cycle-count" element={<CycleCount />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </Styles.Main>
          </Styles.Shell>
        </AppDataProvider>
      </AppInsightsContext.Provider>
    </ConfigProvider>
  );
}

export default App;
